// =====================================================
// FuMind_GM HR Module - HR Settings Service
// Phase 3F.17F.3 - Generic Add/Edit Service
// Backend: Power Automate + SharePoint now; Dataverse/SQL/API later
// =====================================================

const HR_SETTINGS_ACTION_FLOW_URL = "/tracka-flow/v1/HR_SETTINGS_ACTION";
const API_KEY = "";

// =====================================================
// Common Helpers
// =====================================================
async function parseJsonResponse(response) {
  const text = await response.text();
  if (!text) return {};

  try {
    return JSON.parse(text);
  } catch {
    return { message: text, rawText: text };
  }
}

function normalizePowerAutomateResponse(rawData) {
  return rawData?.body || rawData || {};
}

function throwIfFlowFailed(response, data, fallbackMessage) {
  if (!response.ok || data?.success === false) {
    const flowMessage =
      data?.message || data?.error?.message || data?.rawText || "";
    const statusText = `${response.status} ${response.statusText}`.trim();

    throw new Error(
      flowMessage
        ? `${fallbackMessage}: ${flowMessage}`
        : `${fallbackMessage}. HTTP ${statusText}`
    );
  }
}

function ensureConfigured() {
  if (
    !HR_SETTINGS_ACTION_FLOW_URL ||
    HR_SETTINGS_ACTION_FLOW_URL.includes("PASTE_")
  ) {
    throw new Error("HR Settings Action flow URL not configured.");
  }
}

function cleanPayload(payload = {}) {
  const output = {};

  Object.keys(payload || {}).forEach((key) => {
    if (key === "@odata.etag") return;
    if (key === "odata.etag") return;

    const value = payload[key];

    // Keep false/0 because they are valid values.
    if (value === undefined) return;

    output[key] = value;
  });

  return output;
}

// =====================================================
// Phase 3F.17F.3 - Generic HR Settings Action
// action: add | edit
// entityKey: countries | legalEntities | locations | departments | workUnits | ...future entities
// =====================================================
export async function executeHRSettingAction({
  action,
  entityKey,
  id = "",
  payload = {},
  changedBy = "",
  changeReason = "",
} = {}) {
  ensureConfigured();

  const normalizedAction = String(action || "").toLowerCase().trim();
  const normalizedEntityKey = String(entityKey || "").trim();

  if (!["add", "edit"].includes(normalizedAction)) {
    throw new Error("Invalid HR Settings action. Use add or edit.");
  }

  if (!normalizedEntityKey) {
    throw new Error("Entity key is required.");
  }

  if (normalizedAction === "edit" && !id) {
    throw new Error("Record ID is required for edit.");
  }

  const requestBody = {

    action: normalizedAction,
    entityKey: normalizedEntityKey,
    id: String(id || payload?.id || payload?.ID || ""),
    payload: cleanPayload(payload),
    changedBy,
    changeReason,
    sourceModule: "HRSettings",
  };

  const response = await fetch(HR_SETTINGS_ACTION_FLOW_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(requestBody),
  });

  const rawData = await parseJsonResponse(response);
  const data = normalizePowerAutomateResponse(rawData);

  throwIfFlowFailed(response, data, "HR Settings action failed");

  return data;
}

export async function addHRSettingRecord(entityKey, payload = {}, options = {}) {
  return executeHRSettingAction({
    action: "add",
    entityKey,
    payload,
    changedBy: options.changedBy || "",
    changeReason: options.changeReason || "",
  });
}

export async function editHRSettingRecord(entityKey, id, payload = {}, options = {}) {
  return executeHRSettingAction({
    action: "edit",
    entityKey,
    id,
    payload,
    changedBy: options.changedBy || "",
    changeReason: options.changeReason || "",
  });
}

export default {
  executeHRSettingAction,
  addHRSettingRecord,
  editHRSettingRecord,
};
